/**
 * Room Model
 *
 * Represents a physical room within a practice where appointments can be scheduled.
 * Maps to FHIR Location resource.
 *
 * Rooms are practice-specific physical spaces (e.g., exam rooms, treatment facilities)
 * that can be associated with appointment types. When booking an appointment, users can
 * select both a provider and a room if the appointment type has rooms configured.
 *
 * Key relationships:
 * - A Room belongs to exactly one Practice (via managingOrganization)
 * - A Room can be associated with multiple PracticeAppointmentTypes (via actor array)
 * - Room availability is determined by the PracticeAppointmentTypes it's associated with
 *
 * FHIR Mapping:
 * - Room Name → Location.name
 * - City → Location.address.city
 * - Building → Extension (vibrant.room.building)
 * - Floor → Extension (vibrant.room.floor)
 * - Type → Extension (vibrant.room.type)
 * - Features & Description → Location.description
 * - Practice → Location.managingOrganization
 */

import { type MedplumClient, OperationOutcomeError, resolveId } from "@medplum/core";
import type { Address, Location, Organization, Reference } from "@medplum/fhirtypes";
import {
  type CreateResourceOptions,
  FhirResource,
  type SearchOptions,
  type SubscriptionCallback,
} from "@vibrant/models/fhir/FhirResource";
import type {
  ResourceData,
  ResourcePatch,
  SystemValuePair,
} from "@vibrant/models/fhir/fhir-resource-types";
import { findIdentifier } from "@vibrant/models/fhir/fhir-resource-utils";
import dayjs from "dayjs";
import { addRoomToTeamAction } from "@/actions/cal/add-room-to-team";
import type { CalAdminClient, CalManagedUser } from "@/lib/cal/admin";
import {
  CAL_IDENTIFIER_SYSTEM_TEAM_MEMBERSHIPS,
  CAL_IDENTIFIER_SYSTEM_TEAMS,
  CAL_IDENTIFIER_SYSTEM_USERS,
  CAL_IDENTIFIER_SYSTEM_USERS_EMAIL,
  CAL_IDENTIFIER_SYSTEM_USERS_USERNAME,
  CalClient,
  type CreateScheduleRequest,
} from "@/lib/cal/client";
import { isCalInvalidEmailError, isCalNotFoundError } from "@/lib/errors/cal";
import { createLogger } from "@/lib/logger";
import {
  CalCredentials,
  createCalCredentials,
  patchCalCredentials,
  searchOneCalCredentials,
} from "../provider/CalCredentials";
import { findExtension } from "../utils";
import { Practice } from "./Practice";

const logger = createLogger("Room");
const ROOM_DEFAULT_TIMEZONE = "America/Los_Angeles";

/**
 * Patch type for Room updates.
 * Uses nullable extension values to support removal via patchExtensions.
 */
interface RoomPatch extends ResourcePatch<Location> {
  extension?: Array<
    { url: string; valueString: string | null } | { url: string; valueCode: string }
  >;
  address?: Address;
  managingOrganization?: Reference<Organization>;
  identifier?: SystemValuePair[];
}

/**
 * Room type values for categorizing rooms.
 */
export const RoomType = {
  ExamRoom: "exam-room",
  TreatmentFacility: "treatment-facility",
  Office: "office",
} as const;

export type RoomTypeValue = (typeof RoomType)[keyof typeof RoomType];

/**
 * Human-readable labels for room types.
 */
export const RoomTypeLabels: Record<RoomTypeValue, string> = {
  [RoomType.ExamRoom]: "Exam Room",
  [RoomType.TreatmentFacility]: "Treatment Facility",
  [RoomType.Office]: "Office",
};

export interface RoomSearchQuery {
  /**
   * Filter by practice ID.
   */
  practiceId?: string;
  /**
   * Filter by Cal.com user ID (indexed identifier search).
   */
  calUserId?: number;
}

export interface RoomSearchResult {
  rooms: Room[];
  total: number;
}

export interface CreateRoomData {
  /**
   * The name of the room (required).
   */
  name: string;
  /**
   * The city where the room is located (required).
   */
  city: string;
  /**
   * The building name or identifier (required).
   */
  building: string;
  /**
   * The floor where the room is located (optional).
   */
  floor?: string;
  /**
   * The type of room (required).
   */
  type: RoomTypeValue;
  /**
   * Features and description of the room (optional).
   */
  description?: string;
  /**
   * The practice ID this room belongs to (required).
   */
  practiceId: string;
  /**
   * The practice name for display purposes (optional).
   */
  practiceName?: string;
}

/**
 * Serialized Room type for background jobs.
 */
export type SerializedRoom = Location;

export interface UpdateRoomData {
  /**
   * The name of the room.
   */
  name?: string;
  /**
   * The city where the room is located.
   */
  city?: string;
  /**
   * The building name or identifier.
   */
  building?: string;
  /**
   * The floor where the room is located.
   */
  floor?: string | null;
  /**
   * The type of room.
   */
  type?: RoomTypeValue;
  /**
   * Features and description of the room.
   */
  description?: string | null;
  /**
   * The practice ID this room belongs to.
   */
  practiceId?: string;
  /**
   * The practice name for display purposes (optional).
   */
  practiceName?: string;
  /**
   * The Cal.com user ID for this room.
   */
  calUserId?: number | null;
  /**
   * The Cal.com username for this room.
   */
  calUsername?: string | null;
  /**
   * The Cal.com managed email for this room.
   */
  calManagedEmail?: string | null;
  /**
   * The Cal.com team ID this room belongs to.
   */
  calTeamId?: number | null;
  /**
   * The Cal.com team membership ID for this room.
   */
  calTeamMembershipId?: number | null;
}

function formatFloorDisplay({ floor }: { floor?: string }): string | undefined {
  const trimmedFloor = floor?.trim();
  if (!trimmedFloor) {
    return undefined;
  }

  return /\bfloor\b/i.test(trimmedFloor) ? trimmedFloor : `${trimmedFloor} floor`;
}

function buildRoomLocationDisplay({
  name,
  building,
  floor,
  city,
  includeName,
}: {
  name?: string;
  building?: string;
  floor?: string;
  city?: string;
  includeName: boolean;
}): string {
  const parts = [
    includeName ? name?.trim() : undefined,
    building?.trim(),
    formatFloorDisplay({ floor }),
    city?.trim(),
  ].filter((value): value is string => Boolean(value));

  return parts.join(", ");
}

/**
 * Room represents a physical room within a practice where appointments can be scheduled.
 * Maps to FHIR Location resource.
 */
export class Room extends FhirResource<Location> {
  static readonly ResourceType = "Location" as const;
  static readonly isSyncFrom = ["cal"];

  static readonly Identifier = {
    /**
     * Identifier system for the practice ID that owns this room.
     */
    Practice: "vibrant.room.practiceId",
    /**
     * Marker identifier to distinguish Room locations from other Location resources.
     */
    RoomMarker: "vibrant.room.marker",
  } as const;

  static readonly Extension = {
    /**
     * The building where the room is located.
     */
    Building: "vibrant.room.building",
    /**
     * The floor where the room is located (optional).
     */
    Floor: "vibrant.room.floor",
    /**
     * The type of room (exam-room, treatment-facility).
     */
    Type: "vibrant.room.type",
  } as const;

  /**
   * Marker value to identify this Location as a Room.
   */
  static readonly RoomMarkerValue = "practice-room" as const;

  //-----------------------------------------------------------------------------------------------
  //                                         GETTERS
  //-----------------------------------------------------------------------------------------------

  /**
   * The name of the room.
   */
  get name(): string {
    return this.resource.name ?? "Unnamed Room";
  }

  /**
   * Human-readable display name for the room.
   */
  get display(): string {
    return this.name;
  }

  /**
   * The city where the room is located.
   */
  get city(): string | undefined {
    return this.resource.address?.city;
  }

  /**
   * The building where the room is located.
   */
  get building(): string | undefined {
    const extension = findExtension(this.resource, Room.Extension.Building);
    return extension?.valueString;
  }

  /**
   * The floor where the room is located (optional).
   */
  get floor(): string | undefined {
    const extension = findExtension(this.resource, Room.Extension.Floor);
    return extension?.valueString;
  }

  /**
   * Full room location used on appointments and detail views.
   */
  get locationDisplay(): string {
    return buildRoomLocationDisplay({
      name: this.name,
      building: this.building,
      floor: this.floor,
      city: this.city,
      includeName: true,
    });
  }

  /**
   * Booking-flow location details without duplicating the room name.
   */
  get bookingLocationDisplay(): string {
    return buildRoomLocationDisplay({
      name: this.name,
      building: this.building,
      floor: this.floor,
      city: this.city,
      includeName: false,
    });
  }

  /**
   * The type of room.
   */
  get roomType(): RoomTypeValue | undefined {
    const extension = findExtension(this.resource, Room.Extension.Type);
    return extension?.valueCode as RoomTypeValue | undefined;
  }

  /**
   * The human-readable label for the room type.
   */
  get roomTypeLabel(): string | undefined {
    const type = this.roomType;
    return type ? RoomTypeLabels[type] : undefined;
  }

  /**
   * Features and description of the room.
   */
  get description(): string | undefined {
    return this.resource.description;
  }

  /**
   * The ID of the practice this room belongs to.
   * Extracted from managingOrganization reference.
   */
  get practiceId(): string | undefined {
    if (!this.resource.managingOrganization?.reference) {
      return undefined;
    }
    return resolveId(this.resource.managingOrganization);
  }

  /**
   * Reference to the managing organization (practice).
   */
  get managingOrganizationReference(): Reference<Organization> | undefined {
    return this.resource.managingOrganization;
  }

  /**
   * The FHIR Location status (active, inactive, suspended).
   */
  get status(): Location["status"] {
    return this.resource.status;
  }

  /**
   * Whether the room is active.
   */
  get isActive(): boolean {
    return this.resource.status === "active";
  }

  /**
   * The underlying FHIR Location resource.
   */
  get location(): Location {
    return this.resource;
  }

  /**
   * Serializes the room for background jobs.
   */
  serialize(): SerializedRoom {
    return this.resource;
  }

  //-----------------------------------------------------------------------------------------------
  //                                      CAL.COM GETTERS
  //-----------------------------------------------------------------------------------------------

  /**
   * The Cal.com user ID for this room.
   */
  get calUserId(): number | null {
    const identifier = findIdentifier(this.resource, CAL_IDENTIFIER_SYSTEM_USERS);
    if (!identifier?.value) {
      return null;
    }

    const parsed = parseInt(identifier.value, 10);
    if (Number.isNaN(parsed)) {
      throw new Error(`Invalid Cal.com user ID for room ${this.id}: ${identifier.value}`);
    }
    return parsed;
  }

  /**
   * The Cal.com username for this room.
   */
  get calUsername(): string | null {
    const identifier = findIdentifier(this.resource, CAL_IDENTIFIER_SYSTEM_USERS_USERNAME);
    return identifier?.value ?? null;
  }

  /**
   * The Cal.com managed email for this room.
   */
  get calManagedEmail(): string | null {
    const identifier = findIdentifier(this.resource, CAL_IDENTIFIER_SYSTEM_USERS_EMAIL);
    return identifier?.value ?? null;
  }

  /**
   * The Cal.com team ID this room belongs to.
   */
  get calTeamId(): number | null {
    const identifier = findIdentifier(this.resource, CAL_IDENTIFIER_SYSTEM_TEAMS);
    if (!identifier?.value) {
      return null;
    }
    const parsed = parseInt(identifier.value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  /**
   * The Cal.com team membership ID for this room.
   */
  get calTeamMembershipId(): number | null {
    const identifier = findIdentifier(this.resource, CAL_IDENTIFIER_SYSTEM_TEAM_MEMBERSHIPS);
    if (!identifier?.value) {
      return null;
    }
    const parsed = parseInt(identifier.value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  /**
   * Generates a synthetic email for creating a Cal.com user for this room.
   * Format: room-{roomId}@rooms.vibrant.health
   */
  get syntheticEmail(): string {
    return `room-${this.id}@rooms.vibrant.health`;
  }

  //-----------------------------------------------------------------------------------------------
  //                                      STATIC METHODS
  //-----------------------------------------------------------------------------------------------

  /**
   * Get a room by ID.
   *
   * @param medplum - The Medplum client.
   * @param id - The room ID.
   * @returns The room instance.
   * @throws Error if the Location is not a room (missing room marker identifier).
   */
  static async getById(medplum: MedplumClient, id: string): Promise<Room> {
    const location = await medplum.readResource("Location", id);

    // Validate that this Location is actually a room by checking for the room marker identifier
    const roomMarker = findIdentifier(location, Room.Identifier.RoomMarker);
    if (!roomMarker || roomMarker.value !== Room.RoomMarkerValue) {
      throw new Error(
        `Location ${id} is not a room. Missing or invalid room marker identifier (${Room.Identifier.RoomMarker}=${Room.RoomMarkerValue})`,
      );
    }

    return new Room(location);
  }

  /**
   * Creates a new room.
   *
   * @param medplum - The Medplum client.
   * @param data - The room data.
   * @returns The created room.
   */
  static async create(
    medplum: MedplumClient,
    data: CreateRoomData,
    options?: CreateResourceOptions,
  ): Promise<Room> {
    if (!data.name) throw new Error("name is required");
    if (!data.city) throw new Error("city is required");
    if (!data.building) throw new Error("building is required");
    if (!data.type) throw new Error("type is required");
    if (!data.practiceId) throw new Error("practiceId is required");

    const location = await Room.createResource(medplum, Room.toFhirCreateObject(data), options);
    return new Room(location);
  }

  /**
   * Updates a room with the provided data.
   *
   * @param medplum - The Medplum client.
   * @param id - The ID of the room to update.
   * @param data - The data to update.
   * @returns The updated room.
   */
  static async update(medplum: MedplumClient, id: string, data: UpdateRoomData): Promise<Room> {
    const existing = await Room.getById(medplum, id);
    const patch = Room.toFhirUpdateObject(existing.resource, data);
    const location = await Room.patchResource(medplum, { id, patch });
    return new Room(location);
  }

  /**
   * Searches for rooms matching the query.
   *
   * @param medplum - The Medplum client.
   * @param query - Query parameters for filtering.
   * @param options - Search options for pagination and sorting.
   * @returns Array of rooms.
   */
  static async search(
    medplum: MedplumClient,
    query: RoomSearchQuery,
    options?: SearchOptions,
  ): Promise<Room[]> {
    const resources = await Room.searchResources(medplum, Room.toFhirQuery(query), options);
    return resources.map((location) => new Room(location));
  }

  /**
   * Searches for rooms with total count.
   *
   * @param medplum - The Medplum client.
   * @param query - Query parameters for filtering.
   * @param options - Search options for pagination and sorting.
   * @returns Rooms and total count.
   */
  static async searchWithTotal(
    medplum: MedplumClient,
    query: RoomSearchQuery,
    options?: SearchOptions,
  ): Promise<RoomSearchResult> {
    const fhirQuery = Room.toFhirQuery(query);
    const [rooms, total] = await Promise.all([
      Room.search(medplum, query, options),
      Room.getCount(medplum, fhirQuery, options),
    ]);

    return { rooms, total };
  }

  /**
   * Search all rooms matching the given query parameters.
   * Handles pagination internally to fetch all results.
   * Note: Limited to maxPages iterations to prevent excessive memory/CPU use.
   *
   * @param medplum - The Medplum client.
   * @param query - Query parameters for filtering.
   * @returns Array of all rooms matching the query.
   */
  static async searchAll(medplum: MedplumClient, query: RoomSearchQuery): Promise<Room[]> {
    const pageSize = 100;
    const maxPages = 100; // Safeguard to prevent infinite loops on very large result sets
    const allRooms: Room[] = [];
    let offset = 0;
    let iterations = 0;

    while (true) {
      const rooms = await Room.search(medplum, query, {
        limit: pageSize,
        offset,
      });

      allRooms.push(...rooms);

      if (rooms.length < pageSize) {
        // No more pages
        break;
      }

      offset += pageSize;
      iterations += 1;

      if (iterations >= maxPages) {
        logger.warn(
          `searchAll reached maxPages (${maxPages}) limit. ` +
            `Returning ${allRooms.length} rooms, but there may be more.`,
        );
        break;
      }
    }

    return allRooms;
  }

  /**
   * Search all rooms by practice.
   * Handles pagination internally to fetch all results.
   *
   * @param medplum - The Medplum client.
   * @param practiceId - The practice ID to filter by.
   * @returns Array of all rooms for the practice.
   */
  static async searchAllByPractice(medplum: MedplumClient, practiceId: string): Promise<Room[]> {
    return Room.searchAll(medplum, { practiceId });
  }

  /**
   * Subscribe to room changes based on a given query.
   *
   * @param medplum - The Medplum client.
   * @param query - Query parameters for filtering.
   * @param callback - Callback function for subscription events.
   */
  static subscribe(
    medplum: MedplumClient,
    query: RoomSearchQuery,
    callback: SubscriptionCallback<Room>,
  ) {
    return Room.subscribeToResource(medplum, Room.toFhirQuery(query), (resource, metadata) =>
      callback(new Room(resource), metadata),
    );
  }

  //-----------------------------------------------------------------------------------------------
  //                                    INSTANCE METHODS
  //-----------------------------------------------------------------------------------------------

  /**
   * Deactivates the room (sets status to inactive).
   *
   * @param medplum - The Medplum client.
   * @returns The updated room.
   */
  async deactivate(medplum: MedplumClient): Promise<Room> {
    const location = await Room.patchResource(medplum, {
      id: this.id,
      patch: { status: "inactive" },
    });
    return new Room(location);
  }

  /**
   * Activates the room (sets status to active).
   *
   * @param medplum - The Medplum client.
   * @returns The updated room.
   */
  async activate(medplum: MedplumClient): Promise<Room> {
    const location = await Room.patchResource(medplum, {
      id: this.id,
      patch: { status: "active" },
    });
    return new Room(location);
  }

  /**
   * Gets the Cal.com credentials for this room.
   *
   * @param medplum - The Medplum client.
   * @returns The Cal credentials if found, undefined otherwise.
   */
  async getCalCredentials(medplum: MedplumClient): Promise<CalCredentials | undefined> {
    return searchOneCalCredentials(medplum, {
      roomId: this.id,
    });
  }

  //-----------------------------------------------------------------------------------------------
  //                                      CAL.COM METHODS
  //-----------------------------------------------------------------------------------------------

  /**
   * Resolves the timezone used for room scheduling.
   * Falls back to America/Los_Angeles when practice timezone is unavailable.
   */
  private static async resolveRoomTimezone(medplum: MedplumClient, room: Room): Promise<string> {
    if (!room.practiceId) {
      return ROOM_DEFAULT_TIMEZONE;
    }

    try {
      const practice = await Practice.getById(medplum, room.practiceId);
      return practice.timezone ?? ROOM_DEFAULT_TIMEZONE;
    } catch (error) {
      logger.warn(`Failed to fetch practice timezone for room ${room.id}`, { error });
      return ROOM_DEFAULT_TIMEZONE;
    }
  }

  /**
   * Loads the managed Cal.com user for a room.
   * Tries efficient email lookups first, then falls back to ID.
   */
  static async getManagedUserForRoom(
    calAdmin: CalAdminClient,
    room: Room,
  ): Promise<CalManagedUser["user"] | null> {
    const emailCandidates = Array.from(
      new Set(
        [room.calManagedEmail, room.syntheticEmail].filter((email): email is string => !!email),
      ),
    );

    for (const email of emailCandidates) {
      try {
        const byEmail = await calAdmin.findManagedUserByEmail(email);
        if (!byEmail) {
          continue;
        }

        if (room.calUserId && byEmail.id !== room.calUserId) {
          logger.warn("Resolved room Cal user by email with mismatched stored calUserId", {
            roomId: room.id,
            storedCalUserId: room.calUserId,
            resolvedCalUserId: byEmail.id,
            email,
          });
        }
        return byEmail;
      } catch (error) {
        const isInvalidEmailLookup = isCalInvalidEmailError(error);
        if (!isInvalidEmailLookup) {
          throw error;
        }

        // Some legacy room users were created with emails Cal no longer accepts in lookup filters.
        // Skip those lookups and recover via the stored Cal user ID below.
        logger.warn("Skipping invalid room managed-user email during Cal lookup", {
          roomId: room.id,
          practiceId: room.practiceId,
          calUserId: room.calUserId,
          email,
        });
      }
    }

    if (!room.calUserId) {
      return null;
    }

    try {
      return await calAdmin.getManagedUser(room.calUserId);
    } catch (error) {
      logger.warn("Unable to resolve room managed Cal user by ID", {
        roomId: room.id,
        calUserId: room.calUserId,
        emailCandidates,
        error,
      });
      return null;
    }
  }

  /**
   * Ensures there is a Cal.com user for this room.
   * Creates a new managed user if one doesn't exist, or refreshes tokens if needed.
   *
   * @param medplum - The Medplum client.
   * @param calAdmin - The Cal.com admin client.
   * @param roomId - The ID of the room.
   * @returns The room and its Cal credentials.
   */
  static async ensureCalendar(
    medplum: MedplumClient,
    calAdmin: CalAdminClient,
    roomId: string,
  ): Promise<{ room: Room; calCredentials?: CalCredentials }> {
    const room = await Room.getById(medplum, roomId);
    const roomTimezone = await Room.resolveRoomTimezone(medplum, room);

    // Check if room already has Cal credentials
    let calCredentials = await searchOneCalCredentials(medplum, {
      roomId,
    });

    if (calCredentials) {
      const accessTokenExpiresAt = dayjs(calCredentials.accessTokenExpiresAt);
      const tenMinutesFromNow = dayjs().add(10, "minutes");

      let _accessTokenToUse = calCredentials.accessToken;
      let _accessTokenExpiresAtToUse = calCredentials.accessTokenExpiresAt;
      let _refreshTokenToUse = calCredentials.refreshToken;

      if (accessTokenExpiresAt.isBefore(tenMinutesFromNow)) {
        if (!room.calUserId) {
          throw new Error("Room has cal credentials but no cal user ID");
        }
        // Refresh the token
        const refreshedTokens = await calAdmin.refreshAccessToken({
          refreshToken: calCredentials.refreshToken,
          calUserId: room.calUserId,
        });

        // Update stored credentials with new tokens
        const refreshedCalCredentials = await patchCalCredentials(medplum, {
          id: calCredentials.id,
          props: {
            accessToken: refreshedTokens.accessToken,
            accessTokenExpiresAt: refreshedTokens.accessTokenExpiresAt,
            refreshToken: refreshedTokens.refreshToken,
          },
        });

        calCredentials = refreshedCalCredentials;
        _accessTokenToUse = refreshedCalCredentials.accessToken;
        _accessTokenExpiresAtToUse = refreshedCalCredentials.accessTokenExpiresAt;
        _refreshTokenToUse = refreshedCalCredentials.refreshToken;
      }

      const managedUser = await Room.getManagedUserForRoom(calAdmin, room);
      if (!managedUser) {
        throw new Error(`Failed to find managed Cal.com user for room ${room.id}`);
      }

      return { room, calCredentials };
    }

    // Check if user already exists in Cal.com (by email)
    const existingUser = await calAdmin.findManagedUserByEmail(room.syntheticEmail);

    let calUser: CalManagedUser;
    if (existingUser) {
      logger.info(`Found existing Cal.com user for room ${room.id} (Cal ID: ${existingUser.id})`);

      // User exists, refresh their tokens
      const tokens = await calAdmin.refreshManagedUserTokens(existingUser.id);

      calUser = {
        user: existingUser,
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        accessTokenExpiresAt: tokens.accessTokenExpiresAt,
      };
    } else {
      // Create new Cal.com managed user for the room
      logger.info(`Creating new Cal.com user for room ${room.id}`);

      calUser = await calAdmin.createManagedUser({
        email: room.syntheticEmail,
        name: room.name,
        timeZone: roomTimezone,
        timeFormat: 12,
      });
    }

    // Update room with Cal.com IDs
    const updatedRoom = await Room.update(medplum, room.id, {
      calUserId: calUser.user.id,
      calUsername: calUser.user.username,
      calManagedEmail: calUser.user.email,
    });

    // Create Cal credentials for the room
    calCredentials = await createCalCredentials(medplum, {
      roomId: updatedRoom.id,
      accessToken: calUser.accessToken,
      refreshToken: calUser.refreshToken,
      accessTokenExpiresAt: calUser.accessTokenExpiresAt,
    });

    // Add room to practice Cal.com team if available
    // This allows rooms to use the practice's team event types
    try {
      const practice = updatedRoom.practiceId
        ? await Practice.getById(medplum, updatedRoom.practiceId)
        : null;

      if (practice?.calTeamId) {
        // Use server action to add room to team (keeps CAL_CLIENT_SECRET secure)
        const result = await addRoomToTeamAction({
          roomId: updatedRoom.id,
        });

        if (result.success) {
          logger.info("Room added to Cal.com team during calendar setup", {
            roomId: updatedRoom.id,
            practiceId: practice.id,
            calTeamId: practice.calTeamId,
            calUserId: calUser.user.id,
            membershipId: result.membershipId,
          });
        } else {
          logger.warn("Failed to add room to Cal.com team (non-critical)", {
            roomId: updatedRoom.id,
            error: result.error,
          });
        }
      }
    } catch (error) {
      // Don't fail calendar creation if team membership fails
      logger.warn("Error adding room to Cal.com team (non-critical)", {
        error: error instanceof Error ? error.message : String(error),
        roomId: updatedRoom.id,
      });
    }

    // Refetch room to get updated team membership IDs
    const finalRoom = await Room.getById(medplum, updatedRoom.id);
    return { room: finalRoom, calCredentials };
  }

  /**
   * Updates the room's default Cal.com availability schedule.
   * Creates a new default schedule when none exists or the stored default is missing.
   */
  static async updateAvailability({
    medplum,
    calAdmin,
    roomId,
    scheduleRequest,
  }: {
    medplum: MedplumClient;
    calAdmin: CalAdminClient;
    roomId: string;
    scheduleRequest: CreateScheduleRequest;
  }): Promise<{ scheduleId: number }> {
    const { room, calCredentials } = await Room.ensureCalendar(medplum, calAdmin, roomId);

    if (!calCredentials) {
      throw new Error(`Room ${roomId} is missing Cal.com credentials`);
    }

    const managedUser = await Room.getManagedUserForRoom(calAdmin, room);
    if (!managedUser) {
      throw new Error(`Failed to resolve managed Cal.com user for room ${roomId}`);
    }

    const cal = new CalClient(calCredentials.accessToken);
    const defaultScheduleId = managedUser.defaultScheduleId;
    const request: CreateScheduleRequest = {
      ...scheduleRequest,
      isDefault: true,
    };

    if (defaultScheduleId) {
      try {
        await cal.updateSchedule(defaultScheduleId, request);
        logger.info("Updated room availability schedule", {
          roomId: room.id,
          practiceId: room.practiceId,
          calUserId: managedUser.id,
          scheduleId: defaultScheduleId,
        });
        return { scheduleId: defaultScheduleId };
      } catch (error) {
        if (!isCalNotFoundError(error)) {
          throw error;
        }

        logger.warn("Room default schedule missing; creating replacement", {
          roomId: room.id,
          practiceId: room.practiceId,
          calUserId: managedUser.id,
          staleScheduleId: defaultScheduleId,
        });
      }
    }

    const createdSchedule = await cal.createSchedule(request);
    logger.info("Created room availability schedule", {
      roomId: room.id,
      practiceId: room.practiceId,
      calUserId: managedUser.id,
      scheduleId: createdSchedule.id,
    });

    return { scheduleId: createdSchedule.id };
  }

  /**
   * Result of the deleteCalUser operation indicating success/failure of each step.
   * - `skipped: true` indicates the step was not needed (e.g., resource already deleted)
   */
  static DeleteCalUserResult: {
    success: boolean;
    removeManagedUser: { success: boolean; skipped?: boolean; error?: Error };
    deleteCredentials: { success: boolean; skipped?: boolean; error?: Error };
    clearRoomFields: { success: boolean; skipped?: boolean; error?: Error };
  };

  /**
   * Deletes the Cal.com user associated with this room.
   *
   * This method is designed to be idempotent and safe for Inngest retries:
   * - "Not found" errors are treated as success (resource already deleted)
   * - Each operation is attempted independently
   * - Partial failures are logged but do not throw (returns structured result)
   *
   * @param medplum - The Medplum client.
   * @param calAdmin - The Cal.com admin client.
   * @returns A result object indicating success/failure of each cleanup step.
   *          Check `result.success` to determine if all steps completed successfully.
   */
  async deleteCalUser(
    medplum: MedplumClient,
    calAdmin: CalAdminClient,
  ): Promise<typeof Room.DeleteCalUserResult> {
    const result: typeof Room.DeleteCalUserResult = {
      success: true,
      removeManagedUser: { success: true },
      deleteCredentials: { success: true },
      clearRoomFields: { success: true },
    };

    if (!this.calUserId) {
      logger.info(`Room ${this.id} has no Cal.com user to delete`);
      return result;
    }

    // Step 1: Delete the Cal.com managed user (idempotent - treats "not found" as success)
    try {
      await calAdmin.removeManagedUser(this.calUserId);
      logger.info(`Deleted Cal.com user ${this.calUserId} for room ${this.id}`);
    } catch (error) {
      // Treat "not found" as success - the user is already deleted
      if (isCalNotFoundError(error)) {
        logger.info(
          `Cal.com user ${this.calUserId} for room ${this.id} already deleted (not found)`,
        );
        result.removeManagedUser = { success: true, skipped: true };
      } else {
        const err = error instanceof Error ? error : new Error(String(error));
        result.removeManagedUser = { success: false, error: err };
        result.success = false;
        logger.error(
          `Failed to remove Cal.com managed user ${this.calUserId} for room ${this.id}`,
          { error },
        );
      }
    }

    // Step 2: Delete Cal credentials (idempotent - only deletes if found)
    try {
      const calCredentials = await searchOneCalCredentials(medplum, { roomId: this.id });
      if (calCredentials) {
        await Room.deleteCalCredentialsSafe(medplum, calCredentials.id);
        logger.info(`Deleted Cal credentials for room ${this.id}`);
      } else {
        logger.info(
          `No Cal credentials found for room ${this.id} (already deleted or never created)`,
        );
        result.deleteCredentials = { success: true, skipped: true };
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      result.deleteCredentials = { success: false, error: err };
      result.success = false;
      logger.error(`Failed to delete Cal credentials for room ${this.id}`, { error });
    }

    // Step 3: Clear Cal.com IDs from the room (idempotent - treats "not found" as success)
    try {
      await Room.update(medplum, this.id, {
        calUserId: null,
        calUsername: null,
        calManagedEmail: null,
        calTeamId: null,
        calTeamMembershipId: null,
      });
      logger.info(`Cleared Cal.com fields from room ${this.id}`);
    } catch (error) {
      // Treat "not found" as success - the room is already deleted
      if (Room.isMedplumNotFoundError(error)) {
        logger.info(`Room ${this.id} already deleted (not found), skipping field clearing`);
        result.clearRoomFields = { success: true, skipped: true };
      } else {
        const err = error instanceof Error ? error : new Error(String(error));
        result.clearRoomFields = { success: false, error: err };
        result.success = false;
        logger.error(`Failed to clear Cal.com fields from room ${this.id}`, { error });
      }
    }

    // Log summary for debugging (don't throw - let caller decide what to do)
    if (!result.success) {
      logger.warn(`Partial failure deleting Cal.com user for room ${this.id}`, { result });
    }

    return result;
  }

  /**
   * Checks if an error indicates a Medplum resource was not found (already deleted).
   */
  private static isMedplumNotFoundError(error: unknown): boolean {
    if (!(error instanceof OperationOutcomeError)) {
      return false;
    }
    const issue = error.outcome?.issue?.[0];
    return issue?.code === "not-found";
  }

  /**
   * Safely deletes Cal credentials, treating "not found" as success.
   */
  private static async deleteCalCredentialsSafe(
    medplum: MedplumClient,
    credentialsId: string,
  ): Promise<void> {
    try {
      await CalCredentials.delete(medplum, credentialsId);
    } catch (error) {
      // If the credentials were already deleted, that's fine
      if (Room.isMedplumNotFoundError(error)) {
        return;
      }
      throw error;
    }
  }

  //-----------------------------------------------------------------------------------------------
  //                                       FHIR HELPERS
  //-----------------------------------------------------------------------------------------------

  private static toFhirCreateObject(data: CreateRoomData): ResourceData<Location> {
    const extensions: Array<
      { url: string; valueString: string } | { url: string; valueCode: string }
    > = [
      {
        url: Room.Extension.Building,
        valueString: data.building,
      },
      {
        url: Room.Extension.Type,
        valueCode: data.type,
      },
    ];

    if (data.floor) {
      extensions.push({
        url: Room.Extension.Floor,
        valueString: data.floor,
      });
    }

    return {
      name: data.name,
      status: "active",
      mode: "instance",
      description: data.description,
      address: {
        city: data.city,
      },
      physicalType: {
        coding: [
          {
            system: "http://terminology.hl7.org/CodeSystem/location-physical-type",
            code: "ro",
            display: "Room",
          },
        ],
      },
      managingOrganization: {
        reference: `Organization/${data.practiceId}`,
        display: data.practiceName,
      },
      identifier: [
        {
          system: Room.Identifier.RoomMarker,
          value: Room.RoomMarkerValue,
        },
      ],
      extension: extensions,
    };
  }

  private static toFhirUpdateObject(existing: Location, data: UpdateRoomData): RoomPatch {
    // Build extension patches for patchExtensions (which merges, not replaces)
    // To remove an extension, we must explicitly set its value to null
    const extensionPatches: RoomPatch["extension"] = [];

    // Handle building
    if (data.building !== undefined) {
      extensionPatches.push({
        url: Room.Extension.Building,
        valueString: data.building,
      });
    }

    // Handle floor (null removes the extension)
    if (data.floor !== undefined) {
      extensionPatches.push({
        url: Room.Extension.Floor,
        valueString: data.floor,
      });
    }

    // Handle type
    if (data.type !== undefined) {
      extensionPatches.push({
        url: Room.Extension.Type,
        valueCode: data.type,
      });
    }

    // Build the patch object
    const patch: RoomPatch = {};

    if (extensionPatches.length > 0) {
      patch.extension = extensionPatches;
    }

    if (data.name !== undefined) {
      patch.name = data.name;
    }

    if (data.city !== undefined) {
      patch.address = {
        ...existing.address,
        city: data.city,
      };
    }

    if (data.description !== undefined) {
      patch.description = data.description;
    }

    // Handle managingOrganization update
    if (data.practiceId !== undefined) {
      patch.managingOrganization = {
        reference: `Organization/${data.practiceId}`,
        display: data.practiceName,
      };
    }

    // Handle Cal.com identifiers
    const hasCalIdentifierUpdates =
      data.calUserId !== undefined ||
      data.calUsername !== undefined ||
      data.calManagedEmail !== undefined ||
      data.calTeamId !== undefined ||
      data.calTeamMembershipId !== undefined;

    if (hasCalIdentifierUpdates) {
      // Start with existing identifiers, filtering out the ones we're updating
      const calIdentifierSystems = [
        CAL_IDENTIFIER_SYSTEM_USERS,
        CAL_IDENTIFIER_SYSTEM_USERS_USERNAME,
        CAL_IDENTIFIER_SYSTEM_USERS_EMAIL,
        CAL_IDENTIFIER_SYSTEM_TEAMS,
        CAL_IDENTIFIER_SYSTEM_TEAM_MEMBERSHIPS,
      ];

      // Filter existing identifiers and convert to SystemValuePair format
      const preservedIdentifiers: SystemValuePair[] =
        existing.identifier?.flatMap((identifier) => {
          if (!identifier.system || calIdentifierSystems.includes(identifier.system)) {
            return [];
          }
          return [{ system: identifier.system, value: identifier.value ?? null }];
        }) ?? [];

      const newIdentifiers: SystemValuePair[] = [...preservedIdentifiers];

      // Add calUserId identifier (null removes it, undefined preserves existing)
      if (data.calUserId !== undefined) {
        if (data.calUserId !== null) {
          newIdentifiers.push({
            system: CAL_IDENTIFIER_SYSTEM_USERS,
            value: String(data.calUserId),
          });
        }
        // If null, we just don't add it back (effectively removing it)
      } else {
        // Preserve existing calUserId if not being updated
        const existingCalUserId = findIdentifier(existing, CAL_IDENTIFIER_SYSTEM_USERS);
        if (existingCalUserId?.value) {
          newIdentifiers.push({
            system: CAL_IDENTIFIER_SYSTEM_USERS,
            value: existingCalUserId.value,
          });
        }
      }

      // Add calUsername identifier
      if (data.calUsername !== undefined) {
        if (data.calUsername !== null) {
          newIdentifiers.push({
            system: CAL_IDENTIFIER_SYSTEM_USERS_USERNAME,
            value: data.calUsername,
          });
        }
      } else {
        // Preserve existing calUsername if not being updated
        const existingCalUsername = findIdentifier(existing, CAL_IDENTIFIER_SYSTEM_USERS_USERNAME);
        if (existingCalUsername?.value) {
          newIdentifiers.push({
            system: CAL_IDENTIFIER_SYSTEM_USERS_USERNAME,
            value: existingCalUsername.value,
          });
        }
      }

      // Add calManagedEmail identifier
      if (data.calManagedEmail !== undefined) {
        if (data.calManagedEmail !== null) {
          newIdentifiers.push({
            system: CAL_IDENTIFIER_SYSTEM_USERS_EMAIL,
            value: data.calManagedEmail,
          });
        }
      } else {
        // Preserve existing calManagedEmail if not being updated
        const existingCalEmail = findIdentifier(existing, CAL_IDENTIFIER_SYSTEM_USERS_EMAIL);
        if (existingCalEmail?.value) {
          newIdentifiers.push({
            system: CAL_IDENTIFIER_SYSTEM_USERS_EMAIL,
            value: existingCalEmail.value,
          });
        }
      }

      // Add calTeamId identifier
      if (data.calTeamId !== undefined) {
        if (data.calTeamId !== null) {
          newIdentifiers.push({
            system: CAL_IDENTIFIER_SYSTEM_TEAMS,
            value: String(data.calTeamId),
          });
        }
      } else {
        // Preserve existing calTeamId if not being updated
        const existingCalTeamId = findIdentifier(existing, CAL_IDENTIFIER_SYSTEM_TEAMS);
        if (existingCalTeamId?.value) {
          newIdentifiers.push({
            system: CAL_IDENTIFIER_SYSTEM_TEAMS,
            value: existingCalTeamId.value,
          });
        }
      }

      // Add calTeamMembershipId identifier
      if (data.calTeamMembershipId !== undefined) {
        if (data.calTeamMembershipId !== null) {
          newIdentifiers.push({
            system: CAL_IDENTIFIER_SYSTEM_TEAM_MEMBERSHIPS,
            value: String(data.calTeamMembershipId),
          });
        }
      } else {
        // Preserve existing calTeamMembershipId if not being updated
        const existingCalTeamMembershipId = findIdentifier(
          existing,
          CAL_IDENTIFIER_SYSTEM_TEAM_MEMBERSHIPS,
        );
        if (existingCalTeamMembershipId?.value) {
          newIdentifiers.push({
            system: CAL_IDENTIFIER_SYSTEM_TEAM_MEMBERSHIPS,
            value: existingCalTeamMembershipId.value,
          });
        }
      }

      patch.identifier = newIdentifiers;
    }

    return patch;
  }

  static toFhirQuery(
    query: RoomSearchQuery,
  ): Record<string, string | number | boolean | undefined> {
    const searchParams: Record<string, string | number | boolean | undefined> = {
      // Always filter by room marker to only return rooms (not other Location types)
      identifier: `${Room.Identifier.RoomMarker}|${Room.RoomMarkerValue}`,
      status: "active",
    };

    const filterParts: string[] = [];

    // Use _filter for Cal.com user ID to ensure AND semantics with the room marker identifier.
    // Comma-separated identifier values use OR semantics per FHIR spec, which would incorrectly
    // return all rooms (since all rooms have the marker) instead of the specific room with calUserId.
    if (query.calUserId !== undefined) {
      filterParts.push(`identifier eq ${CAL_IDENTIFIER_SYSTEM_USERS}|${query.calUserId}`);
    }

    if (query.practiceId) {
      searchParams.organization = `Organization/${query.practiceId}`;
    }

    // Combine all filter parts with AND semantics
    if (filterParts.length > 0) {
      searchParams._filter = filterParts.join(" and ");
    }

    return searchParams;
  }
}
